import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { ArrowLeft, ArrowRight, CheckCircle, AlertTriangle, Sparkles, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import '@fontsource/jetbrains-mono/400.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaterialsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [project, setProject] = useState(null);
  const [approving, setApproving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/projects/${projectId}/hub-data`, { withCredentials: true })
      .then(r => {
        setProject(r.data.project);
        const mats = r.data.materials;
        // If no materials from DB, show fallback simulated data
        if (mats.length > 0) {
          setMaterials(mats.map((m, i) => ({ ...m, id: m.id || i + 1 })));
        } else {
          setMaterials([
            { id: 1, category: 'Sol principal', detected: 'Non défini', confidence: 0 },
            { id: 2, category: 'Murs principaux', detected: 'Non défini', confidence: 0 },
          ]);
        }
      })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleApprove = async () => {
    setApproving(true);
    await new Promise(r => setTimeout(r, 2000));
    toast.success('Matériauthèque approuvée ! Génération 3D lancée.');
    navigate(`/hub/viewer/${projectId}`);
  };

  const confidenceBadge = (score) => {
    if (score >= 90) return <span className="inline-flex items-center gap-1 text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full text-xs font-mono">{score}%</span>;
    if (score >= 80) return <span className="inline-flex items-center gap-1 text-[#D9A05B] bg-[#D9A05B]/10 px-2 py-0.5 rounded-full text-xs font-mono">{score}%</span>;
    return <span className="inline-flex items-center gap-1 text-red-400 bg-red-400/10 px-2 py-0.5 rounded-full text-xs font-mono"><AlertTriangle className="w-3 h-3" />{score}%</span>;
  };

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#D9A05B] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#09090B] text-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="materials-page">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/projects')} className="text-white/40 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Matériauthèque IA{project ? ` — ${project.name}` : ''}
            </h1>
            <p className="text-xs text-white/40">Étape 2/3 — Vérifiez et ajustez les matériaux détectés</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/30">
          <span className="px-2.5 py-1 rounded-full bg-white/5 text-white/50">1 Upload</span>
          <ArrowRight className="w-3 h-3" />
          <span className="px-2.5 py-1 rounded-full bg-[#D9A05B]/20 text-[#D9A05B] font-bold">2 Matériaux</span>
          <ArrowRight className="w-3 h-3" />
          <span className="px-2.5 py-1 rounded-full bg-white/5">3 Aperçu</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-[#121215] border border-white/5 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Matériaux détectés</p>
            <p className="text-2xl font-semibold text-white mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{materials.length}</p>
          </div>
          <div className="bg-[#121215] border border-white/5 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Confiance moyenne</p>
            <p className="text-2xl font-semibold text-emerald-400 mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {Math.round(materials.reduce((s, m) => s + m.confidence, 0) / materials.length)}%
            </p>
          </div>
          <div className="bg-[#121215] border border-white/5 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-widest text-white/40">À vérifier</p>
            <p className="text-2xl font-semibold text-[#D9A05B] mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {materials.filter(m => m.confidence < 80).length}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#121215] border border-white/5 rounded-xl overflow-hidden mb-6">
          <table className="w-full text-left" data-testid="materials-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-[10px] uppercase tracking-widest text-white/40 font-bold px-5 py-3.5">Catégorie</th>
                <th className="text-[10px] uppercase tracking-widest text-white/40 font-bold px-5 py-3.5">Matériau détecté par l'IA</th>
                <th className="text-[10px] uppercase tracking-widest text-white/40 font-bold px-5 py-3.5 text-center">Confiance</th>
                <th className="text-[10px] uppercase tracking-widest text-white/40 font-bold px-5 py-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {materials.map(mat => (
                <tr key={mat.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`mat-row-${mat.id}`}>
                  <td className="px-5 py-3.5">
                    <span className="text-sm font-medium text-white/80">{mat.category}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    {editingId === mat.id ? (
                      <Input
                        autoFocus
                        defaultValue={mat.detected}
                        className="bg-white/5 border-white/20 text-white h-8 text-sm"
                        onBlur={(e) => {
                          setMaterials(prev => prev.map(m => m.id === mat.id ? { ...m, detected: e.target.value, confidence: 100 } : m));
                          setEditingId(null);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        data-testid={`mat-edit-${mat.id}`}
                      />
                    ) : (
                      <span className="text-sm text-white/60">{mat.detected}</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-center">{confidenceBadge(mat.confidence)}</td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      className="text-white/30 hover:text-[#D9A05B] transition-colors p-1.5 rounded-lg hover:bg-white/5"
                      onClick={() => setEditingId(mat.id)}
                      data-testid={`mat-action-${mat.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Approve */}
        <div className="text-center">
          <button
            className="bg-[#D9A05B] text-black font-semibold px-10 py-4 rounded-xl hover:bg-[#C78D46] transition-all shadow-[0_0_20px_rgba(217,160,91,0.3)] text-base inline-flex items-center gap-3 disabled:opacity-50"
            onClick={handleApprove}
            disabled={approving}
            data-testid="approve-materials-btn"
          >
            {approving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
            {approving ? 'Génération en cours...' : 'Approuver la matériauthèque & Générer la Maquette 3D'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialsPage;
